import { Injectable } from '@angular/core';

import { Action, Selector, State, StateContext } from '@ngxs/store';
import { patch } from '@ngxs/store/operators';
import { of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { Address, Coupon, MyProfile, Order } from 'src/app/interfaces';
import { AuthService, PushNotificationsService } from 'src/app/services';
import {
    ActivateLandingPage,
    AddProfileAddress,
    AuthStateModel,
    DeleteAccount,
    GetMyCoupons,
    GetMyOrders,
    GetPublicToken,
    GetUserProfile,
    HideAuthWidget,
    Login,
    Logout,
    PushNotificationRegister,
    Register,
    RemoveProfileAddress,
    RequestAccountData,
    ResetAllTokens,
    SubscribeToPushTopic,
    SyncAllProducts,
    SyncAllUpdates,
    SyncLandingPage,
    UnsubscribeFromPushTopic
} from './auth.actions';
import { GetFavorites } from '../products';
import { GetCart } from '../cart';

@State<AuthStateModel>({
    name: 'auth',
    defaults: {
        token: null,
        refreshToken: null,
        pbToken: null,
        displayAuthWidget: true,
        syncAll: false,
        syncUpdates: false,
        syncLandingPage: false,
        activateLandingPage: false,
        profile: null,
        orders: [],
        coupons: [],
        ordersPagination: {
            limit: 200,
            page: 1,
            search: '',
            filter: null
        }
    }
})
@Injectable()
export class AuthState {
    constructor(private authService: AuthService, private pushService: PushNotificationsService) {}

    @Selector()
    static displayAuthWidget(state: AuthStateModel): boolean {
        return state.displayAuthWidget;
    }

    @Selector()
    static token(state: AuthStateModel): string | null {
        return state.token;
    }
    @Selector()
    static refreshToken(state: AuthStateModel): string | null {
        return state.refreshToken;
    }
    @Selector()
    static publicToken(state: AuthStateModel): string | null {
        return state.pbToken;
    }

    @Selector()
    static syncingAll(state: AuthStateModel): boolean {
        return state.syncAll;
    }

    @Selector()
    static syncingUpdates(state: AuthStateModel): boolean {
        return state.syncUpdates;
    }

    @Selector()
    static syncingLandingPage(state: AuthStateModel): boolean {
        return state.syncLandingPage;
    }

    @Selector()
    static activatingLandingPage(state: AuthStateModel): boolean {
        return state.activateLandingPage;
    }

    @Selector()
    static isAuthenticated(state: AuthStateModel): boolean {
        // check the token, if not expired
        return !!state.token;
    }

    @Selector()
    static profile(state: AuthStateModel): MyProfile {
        return state.profile?.myProfile;
    }

    @Selector()
    static isPushSubscribed(state: AuthStateModel) {
        return state.profile?.myProfile?.isPushSubscribed;
    }

    @Selector()
    static profileAddresses(state: AuthStateModel): Address[] {
        return state.profile?.myProfile?.addresses;
    }

    @Selector()
    static profileOrders(state: AuthStateModel): Order[] {
        return state.orders;
    }

    @Selector()
    static profileCoupons(state: AuthStateModel): Coupon[] {
        return state.coupons;
    }

    @Action(HideAuthWidget)
    hideAuthWidget({ patchState }: StateContext<AuthStateModel>) {
        patchState({
            displayAuthWidget: false
        });
    }

    @Action(Login)
    login(ctx: StateContext<AuthStateModel>, action: Login) {
        return this.authService.login(action.payload).pipe(
            tap((result) => {
                ctx.patchState({
                    token: result.accessToken
                });
            })
        );
    }

    @Action(Register)
    register(ctx: StateContext<AuthStateModel>, { payload }: Register) {
        return this.authService.register(payload).pipe(
            tap((result) => {
                ctx.patchState({
                    token: result.accessToken
                });
            })
        );
    }

    @Action(GetPublicToken)
    getPublicToken(ctx: StateContext<AuthStateModel>, { deviceId }: GetPublicToken) {
        return this.authService.getPublicAccessToken(deviceId).pipe(
            tap((result) => {
                ctx.patchState({
                    pbToken: result.accessToken
                });
            })
        );
    }

    @Action(Logout)
    logout(ctx: StateContext<AuthStateModel>) {
        ctx.patchState({
            token: null,
            refreshToken: null
        });
        return ctx.dispatch([new GetFavorites(), new GetCart(), new GetUserProfile()]);
    }

    @Action(ResetAllTokens)
    resetAll(ctx: StateContext<AuthStateModel>) {
        ctx.patchState({
            token: null,
            refreshToken: null,
            pbToken: null
        });
    }

    @Action(DeleteAccount)
    deleteAccount(ctx: StateContext<AuthStateModel>) {
        return this.authService.deleteAccount();
    }

    @Action(RequestAccountData)
    requestAccount(ctx: StateContext<AuthStateModel>) {
        return this.authService.requestAccountData();
    }

    @Action(SyncAllProducts)
    syncAll(ctx: StateContext<AuthStateModel>) {
        ctx.patchState({
            syncAll: true
        });
        return this.authService.syncAll().pipe(
            tap(() => {
                ctx.patchState({
                    syncAll: false
                });
            })
        );
    }

    @Action(SyncAllUpdates)
    syncUpdates(ctx: StateContext<AuthStateModel>) {
        ctx.patchState({
            syncUpdates: true
        });
        return this.authService.syncUpdates().pipe(
            tap(() => {
                ctx.patchState({
                    syncUpdates: false
                });
            })
        );
    }

    @Action(SyncLandingPage)
    syncLanding(ctx: StateContext<AuthStateModel>) {
        ctx.patchState({
            syncLandingPage: true
        });
        return this.authService.syncLandingPage().pipe(
            tap(() => {
                ctx.patchState({
                    syncLandingPage: false
                });
            })
        );
    }

    @Action(ActivateLandingPage)
    activateLanding(ctx: StateContext<AuthStateModel>) {
        ctx.patchState({
            activateLandingPage: true
        });
        return this.authService.activateLandingPage().pipe(
            tap(() => {
                ctx.patchState({
                    activateLandingPage: false
                });
            })
        );
    }

    @Action(GetUserProfile)
    getUserProfile(ctx: StateContext<AuthStateModel>) {
        return this.authService.getUserProfile().pipe(
            catchError(() => of(null)),
            tap((result) => {
                ctx.patchState({
                    profile: result
                });
            })
        );
    }

    @Action(AddProfileAddress)
    addAddress(ctx: StateContext<AuthStateModel>, { payload }: AddProfileAddress) {
        return this.authService.addUserProfileAddress(payload).pipe(
            catchError((err) => of(ctx.getState().profile)),
            tap((result) => {
                ctx.patchState({
                    profile: result
                });
            })
        );
    }

    @Action(RemoveProfileAddress)
    removeAddress(ctx: StateContext<AuthStateModel>, { payload }: RemoveProfileAddress) {
        return this.authService.removeUserProfileAddress(payload).pipe(
            catchError((err) => of(ctx.getState().profile)),
            tap((result) => {
                ctx.patchState({
                    profile: result
                });
            })
        );
    }

    @Action(GetMyOrders, { cancelUncompleted: true })
    getMyOrders(ctx: StateContext<AuthStateModel>) {
        return this.authService.getMyOrders(ctx.getState().ordersPagination).pipe(
            tap((response) => {
                ctx.setState(
                    patch({
                        orders: response.docs
                    })
                );
            })
        );
    }

    @Action(GetMyCoupons, { cancelUncompleted: true })
    getMyCoupons(ctx: StateContext<AuthStateModel>) {
        return this.authService.getMyCoupons().pipe(
            tap((response) => {
                ctx.setState(
                    patch({
                        coupons: response
                    })
                );
            })
        );
    }

    @Action(PushNotificationRegister, { cancelUncompleted: true })
    registerPushNotifications(ctx: StateContext<AuthStateModel>, { payload }: PushNotificationRegister) {
        return this.pushService.registerDeviceOnServer(payload).pipe(
            tap((response) => {
                ctx.patchState({
                    profile: response
                });
            })
        );
    }
    @Action(SubscribeToPushTopic, { cancelUncompleted: true })
    subscribeToPushTopic(ctx: StateContext<AuthStateModel>) {
        return this.pushService.subscribeUserToTopic().pipe(
            tap((response) => {
                ctx.patchState({
                    profile: response
                });
            })
        );
    }
    @Action(UnsubscribeFromPushTopic, { cancelUncompleted: true })
    unsubscribeFromPushTopic(ctx: StateContext<AuthStateModel>) {
        return this.pushService.unsubscribeUserFromTopic().pipe(
            tap((response) => {
                ctx.patchState({
                    profile: response
                });
            })
        );
    }
}
